import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import Button from '@repo/ui/button'
import { ExclamationMarkIcon, InformationIcon, XIcon } from '@repo/ui/icons'
import InnerBox from '@repo/ui/inner-box'
import Modal from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { useEffect } from 'react'
import { FieldValues, useFormContext } from 'react-hook-form'

import WavyCircleIcon from '../../../../../packages/ui/icons/wavy-circle'
import { useModalManager } from '../../lib/hooks/use-modal-manager'

const FormError = <T extends FieldValues>() => {
  const form = useFormContext<T>()

  const { toggleModal, modalState } = useModalManager(['errors'])

  useEffect(() => {
    if (
      form.formState.submitCount > 0 &&
      Object.keys(form.formState.errors).length > 0 &&
      !form.formState.isSubmitSuccessful
    ) {
      toggleModal('errors')
    }
  }, [
    form.formState.errors,
    form.formState.submitCount,
    form.formState.isSubmitSuccessful,
    toggleModal
  ])

  return (
    <Modal
      onCancel={() => toggleModal('errors')}
      isOpen={modalState.errors}
      icon={InformationIcon}
      hideActions
      hideHeader
      size="medium"
    >
      <div className="modal-body">
        <div className="flex items-end justify-end w-full">
          <button
            aria-label="Close"
            className={cn(
              'p-1 hover:bg-grey-light-hover/50 rounded-full transition-colors duration-200 ease-in-out '
            )}
            onClick={() => {
              toggleModal('errors')
            }}
          >
            <XIcon color={tailwindColors.grey.light.active} height="12px" width="12px" />
          </button>
        </div>

        <div className="flex items-center justify-center w-full ">
          <div className="relative flex items-center justify-center bg-red-light w-fit p-4 rounded-3xl">
            <WavyCircleIcon color={tailwindColors.red.DEFAULT} />
            <div className="absolute flex items-center justify-center">
              <InformationIcon color={tailwindColors.white.DEFAULT} height="32px" width="32px" />
            </div>
          </div>
        </div>
        <div className="h-6" />
        <div className="flex flex-col justify-center items-center gap-3 ">
          <Typography variant="text" color={tailwindColors.grey.DEFAULT}>
            Veuillez corriger les erreurs pour continuer
          </Typography>
          {form.formState.errors && (
            <InnerBox className="w-full flex flex-col gap-2">
              {Object.entries(form.formState.errors)
                .slice(0, 10) // Only display the first 10 errors
                .map(([key, value]) => {
                  // Narrow down the type for message
                  const errorMessage = typeof value?.message === 'string' ? value.message : null

                  return (
                    <div className="flex items-center gap-2" key={key}>
                      <div className="rounded-full bg-red-light p-[2px]">
                        <div className="flex items-center justify-center border border-red rounded">
                          <ExclamationMarkIcon
                            color={tailwindColors.red.DEFAULT}
                            height="15"
                            width="15"
                          />
                        </div>
                      </div>

                      <Typography variant="secondary-body" color={tailwindColors.grey.DEFAULT}>
                        {errorMessage ?? 'Veuillez corriger les erreurs pour continuer'}
                      </Typography>
                    </div>
                  )
                })}

              {Object.keys(form.formState.errors).length > 10 && (
                <Typography variant="secondary-body" color={tailwindColors.grey.DEFAULT}>
                  ...Et plus
                </Typography>
              )}
            </InnerBox>
          )}
        </div>
        <div className="h-6" />
        <div className="w-full flex items-center gap-2">
          <Button
            label="Fermer"
            variant="ghost"
            className="w-full"
            onClick={() => {
              toggleModal('errors')
            }}
          />
          <Button
            label="Vérifier les erreurs"
            variant="destructive"
            className="w-full"
            onClick={() => {
              toggleModal('errors')
            }}
          />
        </div>
        <div className="h-5" />
      </div>
    </Modal>
  )
}

export default FormError
